// LeaveApply.js - Pure Logic & Architecture
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  FileText,
  Send,
  ArrowLeft,
  Info,
  CheckCircle2,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { applyLeave } from '../../services/leaveService';
import './LeaveApply.css';

const LeaveApply = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  // Logic to calculate days for the UI
  // eslint-disable-next-line no-unused-vars
  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays > 0 ? diffDays : 0;
  };

  const submit = async () => {
    setError(null);
    setMessage(null);

    if (!startDate || !endDate) {
      return setError('Please select both start and end dates');
    }
    if (new Date(startDate) > new Date(endDate)) {
      return setError('Start date cannot be later than end date');
    }

    try {
      setSubmitting(true);
      await applyLeave({ startDate, endDate, reason });
      setMessage('Your request has been submitted successfully!');

      // Reset form
      setStartDate(''); setEndDate(''); setReason('');

      setTimeout(() => navigate('/employee/leaves'), 1200);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ems-apply-page">
      <div className="ems-apply-container">

        {/* Navigation Header */}
        <div className="ems-nav-header">
          <button
            onClick={() => navigate(-1)}
            className="ems-back-btn"
          >
            <ArrowLeft size={18} /> Back to Leave History
          </button>
          <h1 className="ems-page-title">Request Time Off</h1>
          <p className="ems-page-subtitle">Submit your leave request for manager approval.</p>
        </div>

        {/* TOP SECTION: Information & Context */}
        <div className="ems-leaves-top-grid">

          {/* LEFT: Notes & Calendar */}
          <div className="ems-leaves-left-col">
            {/* CL Note */}
            <div className="cl-entitlement-note" style={{ alignItems: 'flex-start' }}>
              <Info size={20} style={{ marginTop: '2px', flexShrink: 0 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '15px' }}>Note:</span>
                <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <li>You are entitled to <strong>1 Casual Leave (CL)</strong> per month.</li>
                  <li>Leave cannot be carried forward for more than one month.</li>
                  <li>If you do not take leave in a particular month, your leave entitlement for that month will lapse.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* RIGHT: Summary & Tips */}
          <div className="ems-leaves-right-col">

            {/* Quick Tips */}
            <div className="ems-tips-card">
              <h4>
                <Info /> Quick Tips
              </h4>
              <ul className="ems-tips-list">
                <li>Apply at least 2 days in advance.</li>
                <li>Include a specific reason for faster approval.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* BOTTOM SECTION: Application Form */}
        <div className="ems-leaves-bottom-section">
          <div className="ems-form-card">

            <h2 className="ems-card-title">Apply Leave</h2>

            {/* Feedback States */}
            {message && (
              <div className="ems-success-msg">
                <CheckCircle2 size={20} />
                <span>{message}</span>
              </div>
            )}

            {error && (
              <div className="ems-error-msg">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            <div className="ems-form-content">
              {/* Date Selection Row */}
              <div className="ems-date-row">
                <div className="ems-date-field">
                  <label className="ems-field-label ems-start-label">
                    <Calendar /> Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="ems-date-input"
                  />
                </div>

                <div className="ems-date-field">
                  <label className="ems-field-label ems-end-label">
                    <Calendar /> End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="ems-date-input"
                  />
                </div>
              </div>

              {/* Reason Field */}
              <div className="ems-reason-field">
                <label className="ems-field-label">
                  <FileText /> Reason for Leave
                </label>
                <textarea
                  rows={4}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Briefly describe the reason for your absence..."
                  className="ems-textarea"
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={submit}
                disabled={submitting}
                className="ems-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Send size={20} />
                )}
                {submitting ? 'Processing Request...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LeaveApply;
